import React, { FC, useCallback } from 'react';
import moment from 'moment';
import { Box, Table, TableBody, TableHead, TablePagination, TableRow } from '@material-ui/core';
import { Link } from 'react-router-dom';
import Ticket, { TicketClosedReason } from 'src/interfaces/api/Ticket';
import TableCell from 'src/components/TableCell';
import clsx from 'clsx';
import { createUseStyles } from 'src/theming';
import Employee from 'src/interfaces/api/Employee';
import { Skeleton } from '@material-ui/lab';
import InterventionsTime from './components/InterventionsTime';
import ContactView from './components/ContactView';
import { getFormatedDuration } from 'src/utils/TimeUtils';
import Alert from 'src/components/Alert';
import Rating from '@material-ui/lab/Rating';

interface Props {
  loading: boolean;
  hidden?: boolean;
  error?: string;
  showCustomerSatisfaction?: boolean;
  totalInterventionsDuration: number;
  totalInterventionsDurationOnSite: number;
  closedReasons: TicketClosedReason[];
  tickets: Ticket[];
  employees: Employee[];
  employeesLoading: boolean;
  employeesError?: string;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  total: number;
}

const useStyles = createUseStyles({
  inProgress: {
    borderLeft: '4px solid #f57c73',
  },
  toProcess: {
    borderLeft: '4px solid #5b4c91',
  },
});

const TicketTable: FC<Props> = ({
  tickets,
  closedReasons,
  totalInterventionsDuration,
  totalInterventionsDurationOnSite,
  loading,
  error,
  page,
  setPage,
  limit,
  total,
  employees,
  employeesLoading,
  employeesError,
  hidden,
  showCustomerSatisfaction,
  ...props
}) => {
  const classes = useStyles();

  const getClosedReason = useCallback((id?: number) => closedReasons.find((cr) => cr.id === id), [closedReasons]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const sortByDate = (a: Ticket, b: Ticket) => {
    // @ts-ignore
    return new Date(b.createdAt) - new Date(a.createdAt);
  };

  return (
    <div hidden={hidden}>
      {!!totalInterventionsDuration && (
        <Alert mt='10px'>
          Total temps interventions sur cette période:{' '}
          <Box component='span' fontWeight={700}>
            {getFormatedDuration(totalInterventionsDuration + totalInterventionsDurationOnSite)}
          </Box>
          {totalInterventionsDurationOnSite > 0 && (
            <span>
              {` `}(dont {getFormatedDuration(totalInterventionsDurationOnSite)} sur site)
            </span>
          )}
        </Alert>
      )}

      <Table {...props}>
        <TableHead>
          <TableRow>
            <TableCell>Sujet</TableCell>
            <TableCell>Date ouverture</TableCell>
            <TableCell>Date fermeture</TableCell>
            <TableCell>Support</TableCell>
            <TableCell>Responsabilité</TableCell>
            <TableCell>Contact</TableCell>
            {showCustomerSatisfaction ? (
              <TableCell align='right'>Satisfaction client</TableCell>
            ) : (
              <TableCell align='right'>Temps passé</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.sort(sortByDate).map((ticket) => {
            const toProcess = !ticket.closingDate && ticket.stateId === 1;
            const inProgress = !ticket.closingDate && ticket.stateId !== 1;
            const supportEmployee = employees.find((employee) => employee.id === ticket.supportEmployeeId);

            return (
              <TableRow
                key={ticket.id}
                className={clsx({ [classes.toProcess]: toProcess, [classes.inProgress]: inProgress })}
              >
                <TableCell>
                  <Link to={`/dashboard/tickets/${ticket.id}`}>{ticket.subject}</Link>
                </TableCell>
                <TableCell>{moment(ticket.createdAt).format('DD-MM-YYYY')}</TableCell>
                <TableCell>{ticket.closingDate && moment(ticket.closingDate).format('DD-MM-YYYY')}</TableCell>
                <TableCell>
                  {!ticket.supportEmployeeId ? (
                    <span>Non-renseigné</span>
                  ) : supportEmployee ? (
                    <Link to={`/dashboard/user/${supportEmployee.legacyUserId}`}>
                      <span>{`${supportEmployee.firstname} ${supportEmployee.lastname}`}</span>
                    </Link>
                  ) : employeesLoading ? (
                    <Skeleton />
                  ) : (
                    <span>{employeesError}</span>
                  )}
                </TableCell>
                <TableCell>{getClosedReason(ticket.closedReasonId)?.label || ticket.closedReasonId}</TableCell>
                <TableCell>
                  {ticket.associatedContactId && <ContactView contactId={ticket.associatedContactId} />}
                </TableCell>

                {showCustomerSatisfaction ? (
                  <TableCell align='right'>
                    <Rating size='small' value={ticket.customerSatisfactionRating} max={4} precision={0.1} readOnly />
                  </TableCell>
                ) : (
                  <TableCell align='right'>
                    <InterventionsTime ticketId={ticket.id} />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[10]}
        count={total}
        component='div'
        rowsPerPage={limit}
        page={page}
        onPageChange={handleChangePage}
      />
    </div>
  );
};

export default TicketTable;
